import React, { useCallback, useEffect, useRef, useState } from "react";

import jsQR from "jsqr";
import Webcam from "react-webcam";
import { Image } from "semantic-ui-react";

import CheckInLayout from "./CheckInLayout";

import "./GreetingsMonitor.css";

// ======================= Types =======================
type GreetingsMonitorProps = {
  onQrScanned?: (data: string) => void;
};

// ======================= Constants =======================
const SCAN_INTERVAL = 500;
// Avg per-pixel diff above this counts as motion (sensor noise ~5 on stable cameras)
const MOTION_THRESHOLD = 8;
const PREVIEW_IDLE_MS = 2000;

const IMAGES = {
  lineOaName: "/static/images/checkin/lobby/line-oa-name.png",
  lineQr: "/static/images/checkin/lobby/line-qr.png",
  step1Phone: "/static/images/checkin/lobby/step1-phone.png",
  step2MenuIcon: "/static/images/checkin/lobby/step2-menu-icon.png",
  step2PatientQr: "/static/images/checkin/lobby/step2-patient-qr.png",
  scanFrame: "/static/images/checkin/lobby/scan-frame.png",
  tipTubeIcon: "/static/images/checkin/lobby/tip-tube-icon.png",
  bloodDrop: "/static/images/checkin/lobby/blood-drop.png",
  barcodeScanner: "/static/images/checkin/lobby/barcode-scanner.png",
};

const playBeep = () => {
  try {
    const audioCtx = new AudioContext();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.frequency.value = 1200;
    gain.gain.value = 0.3;
    osc.start();
    osc.stop(audioCtx.currentTime + 0.15);
  } catch {}
};

// Sample every 16th pixel — coarse enough for motion detection without scanning the full frame
const computeFrameDiff = (curr: Uint8ClampedArray, prev: Uint8ClampedArray): number => {
  let diff = 0;
  let count = 0;
  for (let i = 0; i < curr.length; i += 64) {
    diff += Math.abs(curr[i] - prev[i]);
    count++;
  }
  return diff / count;
};

const GreetingsMonitor = (props: GreetingsMonitorProps) => {
  const webcamRef = useRef<Webcam>(null);
  const scanTimerRef = useRef<number | null>(null);
  const lastFrameRef = useRef<Uint8ClampedArray | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  // ตรวจสอบว่าเป็น webcam หรือไม่
  const [hasWebcam, setHasWebcam] = useState(false);

  // useEffect(() => {
  //   const checkWebcam = async () => {
  //     const devices = await navigator.mediaDevices.enumerateDevices();

  //     const hasCamera = devices.some((d) => d.kind === "videoinput");

  //     console.log("Webcam connected:", hasCamera);

  //     setHasWebcam(hasCamera);
  //   };

  //   checkWebcam();

  //   navigator.mediaDevices.addEventListener("devicechange", checkWebcam);

  //   return () => {
  //     navigator.mediaDevices.removeEventListener("devicechange", checkWebcam);
  //   };
  // }, []);

  const scanQrCode = useCallback(() => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const prev = lastFrameRef.current;

    if (prev && prev.length === data.length) {
      if (computeFrameDiff(data, prev) > MOTION_THRESHOLD) {
        setShowPreview(true);
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        idleTimerRef.current = window.setTimeout(() => setShowPreview(false), PREVIEW_IDLE_MS);
      }
    }
    lastFrameRef.current = new Uint8ClampedArray(data);

    const code = jsQR(data, imageData.width, imageData.height);
    if (code?.data) {
      playBeep();
      props.onQrScanned?.(code.data);
    }
  }, [props.onQrScanned]);

  useEffect(() => {
    if (!hasWebcam) return;

    scanTimerRef.current = window.setInterval(scanQrCode, SCAN_INTERVAL);
    return () => {
      if (scanTimerRef.current) clearInterval(scanTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [scanQrCode, hasWebcam]);

  useEffect(() => {
    if (hasWebcam) return;

    let buffer = "";

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Enter") {
        const scannedValue = buffer.trim();

        if (scannedValue) {
          playBeep();
          props.onQrScanned?.(scannedValue);
        }

        buffer = "";
        return;
      }

      const char = normalizeKey(e);
      if (!char) return;

      buffer += char;
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [hasWebcam, props.onQrScanned]);

  return (
    <CheckInLayout headerTitle="ขั้นตอนการรับบริการ">
      <div className="lobby-tip">
        <Image src={IMAGES.tipTubeIcon} className="lobby-tip-icon" />
        <div className="lobby-tip-text">
          "ควรดื่มน้ำเปล่าให้เพียงพอก่อนเจาะเลือด เพื่อช่วยให้หาเส้นเลือดได้ง่ายขึ้น
          และลดโอกาสการเกิดหน้ามืด หลังการเจาะเลือด"
        </div>
        <Image src={IMAGES.bloodDrop} className="lobby-tip-drop" />
      </div>

      <div className="lobby-cards">
        <div className="lobby-card">
          <div className="lobby-step-num">1</div>
          <div className="lobby-step-title">
            สแกนเพิ่มเพื่อน
            <br />
            บน LINE Official
          </div>
          <Image src={IMAGES.lineOaName} className="lobby-line-oa" />
          <Image src={IMAGES.lineQr} className="lobby-line-qr" />
          <div className="lobby-step-handle">@mehealth</div>
          <Image src={IMAGES.step1Phone} className="lobby-step1-phone" />
        </div>

        <div className="lobby-card">
          <div className="lobby-step-num">2</div>
          <div className="lobby-step-title">
            เลือกเมนู
            <br />
            เช็คอินด้วย QR
          </div>
          <Image src={IMAGES.step2MenuIcon} className="lobby-step2-menu-icon" />
          <div className="lobby-step-hint">
            นำ QR code ไปแสกน
            <br />
            ในขั้นตอนที่ 3
          </div>
          <Image src={IMAGES.step2PatientQr} className="lobby-step2-patient-qr" />
        </div>

        {hasWebcam ? (
          <div className="lobby-card">
            <div className="lobby-step-num">3</div>
            <div className="lobby-step-title">
              สแกนเพื่อ
              <br />
              ลงทะเบียนรับบริการ
            </div>
            <div className="lobby-step-hint">
              นำ QR code ที่ได้รับ
              <br />
              มาสแกนผ่านกล้องด้านบน
            </div>

            {/* Issue #84847: live feed fades in on motion; placeholder otherwise */}
            <div className={`lobby-camera-box${showPreview ? " is-active" : ""}`}>
              <Webcam
                ref={webcamRef}
                audio={false}
                videoConstraints={{ facingMode: "user", width: 640, height: 480 }}
                className="lobby-camera-video"
              />
              <Image src={IMAGES.scanFrame} className="lobby-scan-frame-overlay" />
            </div>

            <div className="lobby-camera-label">
              <span className="lobby-camera-dot" />
              ภาพจากกล้อง Web Camera
            </div>
          </div>
        ) : (
          <div className="lobby-card">
            <div className="lobby-step-num">3</div>
            <div className="lobby-step-title">
              สแกนเพื่อ
              <br />
              ลงทะเบียนรับบริการ
            </div>
            <div className="lobby-step-hint">
              นำ QR code ที่ได้รับ
              <br />
              มาสแกนผ่านเครื่อง
              <br />
              Barcode Scanner
            </div>
            <Image src={IMAGES.barcodeScanner} />
            <div className="lobby-step-hint">
              เมื่อได้รับหมายเลขคิวแล้ว
              <br />
              กรุณานั่งรอเรียกรับบริการ
              <br />
              ที่พื้นที่นั่งรอค่ะ
            </div>
          </div>
        )}
      </div>
    </CheckInLayout>
  );
};

export default GreetingsMonitor;

const normalizeKey = (e: KeyboardEvent): string | null => {
  const base = baseMap[e.code];
  if (!base) return null;

  // letters
  if (e.code.startsWith("Key")) {
    return e.shiftKey ? base.toUpperCase() : base;
  }

  // digits
  if (e.code.startsWith("Digit")) {
    return e.shiftKey ? (shiftMap[base] ?? base) : base;
  }

  // symbols
  if (shiftMap[base]) {
    return e.shiftKey ? shiftMap[base] : base;
  }

  return base;
};

const baseMap: Record<string, string> = {
  Digit0: "0",
  Digit1: "1",
  Digit2: "2",
  Digit3: "3",
  Digit4: "4",
  Digit5: "5",
  Digit6: "6",
  Digit7: "7",
  Digit8: "8",
  Digit9: "9",

  KeyA: "a",
  KeyB: "b",
  KeyC: "c",
  KeyD: "d",
  KeyE: "e",
  KeyF: "f",
  KeyG: "g",
  KeyH: "h",
  KeyI: "i",
  KeyJ: "j",
  KeyK: "k",
  KeyL: "l",
  KeyM: "m",
  KeyN: "n",
  KeyO: "o",
  KeyP: "p",
  KeyQ: "q",
  KeyR: "r",
  KeyS: "s",
  KeyT: "t",
  KeyU: "u",
  KeyV: "v",
  KeyW: "w",
  KeyX: "x",
  KeyY: "y",
  KeyZ: "z",

  Backquote: "`",
  Minus: "-",
  Equal: "=",
  BracketLeft: "[",
  BracketRight: "]",
  Backslash: "\\",
  Semicolon: ";",
  Quote: "'",
  Comma: ",",
  Period: ".",
  Slash: "/",
  Space: " ",
};

const shiftMap: Record<string, string> = {
  "1": "!",
  "2": "@",
  "3": "#",
  "4": "$",
  "5": "%",
  "6": "^",
  "7": "&",
  "8": "*",
  "9": "(",
  "0": ")",

  "-": "_",
  "=": "+",
  "[": "{",
  "]": "}",
  "\\": "|",
  ";": ":",
  "'": '"',
  ",": "<",
  ".": ">",
  "/": "?",
  "`": "~",
};
